import { LinearGradient } from "expo-linear-gradient";
import { MotiView } from "moti";
import React, { useState, useRef, useLayoutEffect } from "react";
import { Platform, Dimensions } from "react-native";
import { Easing } from "react-native-reanimated";

import { Box } from "@whoosh/design-system/src/components/primitive";
import { Theme } from "@whoosh/design-system/src/theme/restyle/theme";

import { whooshPrimitives } from "../../../../../../theme/colors";
import GradientBackground from "./GradientBackground";

type EventColorOption =
  | "userSelectable.A.100"
  | "userSelectable.B.100"
  | "userSelectable.C.100"
  | "userSelectable.D.100"
  | "userSelectable.E.100"
  | "userSelectable.F.100";

const RotatingGradientBorder = ({
  children,
  color,
}: {
  children: React.JSX.Element;
  color?: keyof Theme["colors"];
}) => {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const outerBoxRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });

  // NOTE: These will change when server colors are updated
  const getEventHexColorFromServerColor = (color: EventColorOption) => {
    switch (color) {
      case "userSelectable.A.100":
        return whooshPrimitives.userSelectable.A[500];
      case "userSelectable.B.100":
        return whooshPrimitives.userSelectable.B[500];
      case "userSelectable.C.100":
        return whooshPrimitives.userSelectable.C[500];
      case "userSelectable.D.100":
        return whooshPrimitives.userSelectable.D[500];
      case "userSelectable.E.100":
        return whooshPrimitives.userSelectable.E[500];
      case "userSelectable.F.100":
        return whooshPrimitives.userSelectable.F[500];
      default:
        return whooshPrimitives.userSelectable.C[500];
    }
  };

  const hexBorderColor = getEventHexColorFromServerColor(
    color as EventColorOption
  );

  useLayoutEffect(() => {
    if (Platform.OS === "web") {
      setWidth(outerBoxRef?.current?.offsetWidth || 500);
      setHeight(outerBoxRef?.current?.offsetHeight || 500);
    } else {
      setDimensions({
        width: Dimensions.get("window").width,
        height: Dimensions.get("window").height,
      });
    }
  }, []);

  const getBoxSize = () => {
    if (Platform.OS === "web") {
      return width > height ? width : height;
    } else {
      return dimensions.width > dimensions.height
        ? dimensions.width
        : dimensions.height;
    }
  };

  const animatedBoxSize = getBoxSize();

  return (
    <Box
      width="100%"
      p={0.5}
      position="relative"
      overflow="hidden"
      ref={outerBoxRef}
      style={{ borderRadius: 18 }}
    >
      <MotiView
        style={{
          width: animatedBoxSize + 200,
          height: animatedBoxSize + 200,
          position: "absolute",
          left: "50%",
          marginLeft: (-1 * (animatedBoxSize + 200)) / 2,
          top: "50%",
          marginTop: (-1 * (animatedBoxSize + 200)) / 2,
        }}
        from={{
          transform: [{ rotate: "0deg" }],
        }}
        animate={{
          transform: [{ rotate: "360deg" }],
        }}
        transition={{
          transform: {
            loop: true,
            type: "timing",
            duration: 2000,
            repeatReverse: false,
            easing: Easing.linear,
          },
        }}
      >
        <GradientBackground
          width={animatedBoxSize + 200}
          height={animatedBoxSize + 200}
          color={hexBorderColor || "black"}
        >
          <LinearGradient
            colors={["black", "white"]}
            start={[0, 0]}
            end={[1, 0]}
            style={{
              position: "absolute",
              height: "100%",
              width: "100%",
              top: 5,
            }}
          />
        </GradientBackground>
      </MotiView>
      {children}
      <Box backgroundColor="grayscale-0" width="100%" flex={1} />
    </Box>
  );
};

export default RotatingGradientBorder;
